import { Helmet, HelmetData } from "react-helmet-async";
import { useRef } from "react";
import { CheckMark } from "../../icons";
import {
  AI_BG_NEW,
  Capabilty_Img_One,
  Capabilty_Img_Three,
  Capabilty_Img_Two,
  Dedicated_Teams_Icon,
  Managed_Services_Icon,
  Map_Overylay_Img,
  Model_Evaluation_Hero_Img,
  Model_What_We_Deliver_Img,
  Model_Why_Choose_Us_Img_Five,
  Model_Why_Choose_Us_Img_Four,
  Model_Why_Choose_Us_Img_One,
  Model_Why_Choose_Us_Img_Six,
  Model_Why_Choose_Us_Img_Three,
  Model_Why_Choose_Us_Img_Two,
  Project_Based_Icon,
  Services_Bg,
  Use_Case_Bg,
} from "../../images";
import BookConsultation from "../book-a-consultation";
import { NewFooter } from "../newFooter";
import useIntersectionObserver from "../../utils/useIntersectionObserver";

export default function ModelEvaluation() {
  const targetRef = useRef(null);

  useIntersectionObserver({ targetRef });

  const helmetData = new HelmetData({});

  const services = [
    {
      title: "AI Model Evaluation",
      description:
        "Thorough testing of language, vision, and custom AI models to ensure accuracy, safety, and reliability.",
      isHighlighted: true,
    },
    {
      title: "Data Annotation & Labeling",
      description:
        "Precise labeling for text, image, video, and audio, delivering clean, consistent data at scale.",
    },
    {
      title: "Quality Assurance & Validation",
      description:
        "Layered checks and expert reviews with over 95% accuracy before final delivery.",
    },
    {
      title: "RLHF & Fine-Tuning",
      description:
        "Human-in-the-loop refinement that helps AI systems learn, adapt, and align with user intent.",
    },
    {
      title: "Prompt Engineering & Optimization",
      description:
        "Smart prompt design and testing that make AI responses sharper, faster, and more relevant.",
    },
    {
      title: "Red Teaming & Safety Testing",
      description:
        "Stress-testing AI to detect bias, errors, and risks, ensuring your models are safe and dependable.",
    },
  ];

  const capabilities = [
    {
      id: 1,
      title: "Text Annotation",
      image: Capabilty_Img_One,
      features: [
        "Text classification and categorization",
        "Named entity recognition (NER)",
        "Semantic segmentation",
        "Question-answer pair creation",
        "Translation quality assessment",
      ],
    },
    {
      id: 2,
      title: "Image & Video Annotation",
      image: Capabilty_Img_Two,
      features: [
        "Object detection and bounding boxes",
        "Image segmentation",
        "Landmark and keypoint annotation",
        "Image classification",
        "OCR verification and correction",
      ],
    },
    {
      id: 3,
      title: "Model Evaluation",
      image: Capabilty_Img_Three,
      features: [
        "Factual accuracy verification",
        "Coherence and relevance scoring",
        "Bias and fairness testing",
        "Safety and toxicity detection",
        "Cultural appropriateness review",
      ],
    },
  ];

  const features = [
    {
      icon: Model_Why_Choose_Us_Img_One,
      title: "Trained Workforce at Scale",
      description:
        "1,000+ trained AI-literate specialists who combine technical expertise with deep data understanding.",
    },
    {
      icon: Model_Why_Choose_Us_Img_Two,
      title: "Context-Aware Approach",
      description:
        "We go beyond labeling, capturing real-world meaning, tone, and nuance in every dataset.",
    },
    {
      icon: Model_Why_Choose_Us_Img_Three,
      title: "Rigorous Quality Control",
      description:
        "We maintain 95%+ accuracy rates through systematic QA processes and continuous improvement.",
    },
    {
      icon: Model_Why_Choose_Us_Img_Four,
      title: "Scalable Infrastructure",
      description:
        "From small pilots to global deployments, we scale seamlessly without compromising standards.",
    },
    {
      icon: Model_Why_Choose_Us_Img_Five,
      title: "Secure & Compliant",
      description:
        "We maintain strict confidentiality and comply with international data protection standards.",
    },
    {
      icon: Model_Why_Choose_Us_Img_Six,
      title: "Speed with Precision",
      description:
        "We deliver speed without sacrificing quality. Our distributed workforce and efficient workflows.",
    },
  ];

  const why = [
    {
      icon: Project_Based_Icon,
      title: "Project-Based",
      text: "Fixed-scope projects with clear deliverables and timelines, ideal for pilots or one-time annotation.",
    },
    {
      icon: Dedicated_Teams_Icon,
      title: "Dedicated Teams",
      text: "Specialists trained in your workflows for continuous, high-quality output.",
    },
    {
      icon: Managed_Services_Icon,
      title: "Managed Services",
      text: "End-to-end data operations management,from infrastructure to quality assurance, we handle it all.",
    },
  ];

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>Model Evaluation &amp; Data Labeling at Scale | AI Data Services Africa | Analytics Intelligence Africa</title>

        <link
          rel="canonical"
          href="https://africa.analyticsintelligence.com/model-evaluation"
        />

        <meta
          name="description"
          content="Analytics Intelligence Africa delivers high-quality data labeling and model evaluation services at scale. 1,000+ expert annotators provide context-aware annotations and RLHF services that power reliable, high-performing AI systems across Africa and globally."
        />

        <meta
          name="keywords"
          content="Analytics Intelligence Africa, model evaluation Africa, data labeling Africa, AI data services Africa, data annotation Africa, RLHF Africa, AI training data Africa, machine learning evaluation Africa, annotation at scale Africa, AI quality assurance Africa, red teaming AI Africa, prompt engineering Africa, text annotation Africa, image annotation Africa"
        />

        <meta property="og:title" content="Model Evaluation & Data Labeling at Scale | Analytics Intelligence Africa" />
        <meta property="og:description" content="1,000+ expert annotators delivering precise data labeling, model evaluation, RLHF, and AI quality assurance at scale — Analytics Intelligence Africa." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://africa.analyticsintelligence.com/model-evaluation" />
        <meta property="og:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Model Evaluation & Data Labeling at Scale | Analytics Intelligence Africa" />
        <meta name="twitter:description" content="1,000+ expert annotators delivering precise data labeling, model evaluation, RLHF, and AI quality assurance at scale — Analytics Intelligence Africa." />
        <meta name="twitter:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Model Evaluation & Data Labeling",
            "url": "https://africa.analyticsintelligence.com/model-evaluation",
            "description": "High-quality data labeling, model evaluation, RLHF, and AI quality assurance services at scale — 1,000+ trained expert annotators across Africa.",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Africa",
              "url": "https://africa.analyticsintelligence.com"
            },
            "areaServed": ["Nigeria", "Ghana", "Kenya", "South Africa", "Rwanda", "Africa"],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "AI Data Services",
              "itemListElement": [
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "AI Model Evaluation"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Data Annotation & Labeling"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "RLHF & Fine-Tuning"}},
                {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Red Teaming & Safety Testing"}}
              ]
            }
          }
        `}</script>
      </Helmet>

      <main>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center bg-left-bottom md:bg-center"
          ref={targetRef}
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16 md:gap-20 lg:gap-28 xl:gap-36  pt-[126px] pb-11 md:py-[96px] py-[96px]">
            <div className="flex basis-2/4 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-8 md:space-y-11 flex flex-col items-center md:items-start">
                <span className="text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  Model Evaluation
                </span>
                <div className="space-y-4">
                  <h2 className="text-4xl md:text-[40px] xl:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white">
                    Model Evaluation & Data <br className="hidden xl:block" />{" "}
                    Labeling at Scale
                  </h2>
                  <p className="text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                    Delivering context-aware annotations and model evaluations
                    that bridge global AI technology with accuracy
                  </p>
                </div>
              </div>
            </div>
            <div className="basis-2/4 flex justify-end">
              <img
                className=" w-full max-w-[680px]"
                src={Model_Evaluation_Hero_Img}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section
          className="relative w-full bg-white py-11 md:py-[80px] xl:py-[100px] overflow-hidden bg-top bg-contain bg-no-repeat"
          style={{
            backgroundImage: `url("${Map_Overylay_Img}")`,
          }}
        >
          <div className="mx-auto container space-y-14">
            <div className="h-[300px] w-[300px] xl:h-[450px] xl:w-[450px] bg-[#7AA8FF] blur-[180px] absolute top-0 -right-[70%] md:-right-[45%] lg:-right-[15%]"></div>
            <div className="space-y-3">
              <span className="text-sm md:text-base bg-[#EDF9FF] px-3 p-[6px] rounded-[100px] text-[#0073FF]">
                What We Deliver
              </span>
              <div className="space-y-5">
                <h1 className="text-2xl xl:text-[32px] font-bold !leading-[120%]">
                  Excellence in Every Data Point
                </h1>
                <p className="text-base md:text-lg xl:text-xl font-medium text-[#333333] max-w-[950px]">
                  We power AI systems with data you can trust. Our 1,000+ expert
                  annotators deliver precise labeling and evaluation services,
                  ensuring every data point drives reliable, high-performing AI
                  outcomes.
                </p>
              </div>
            </div>

            <div className="mb-[32px] md:mb-[100px] flex flex-col items-center gap-8 md:grid md:gap-12 lg:gap[65px] xl:gap-[88px] md:grid-cols-2">
              <div className="">
                {services.map((service, index) => (
                  <div
                    key={index}
                    className={`py-2 ${
                      service.isHighlighted
                        ? "border-l-4 border-blue-600 pl-6"
                        : "border-l-4 border-transparent pl-6"
                    }`}
                  >
                    <h3 className="text-base md:text-lg font-bold mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm md:text-base !leading-[150%]">
                      {service.description}
                    </p>
                  </div>
                ))}
              </div>

              <img
                src={Model_What_We_Deliver_Img}
                alt="Woman using laptop"
                className="h-auto w-full max-w-[640px] object-cover"
              />
            </div>
          </div>
        </section>

        <section
          className="relative py-20 bg-left-bottom md:bg-center bg-cover md:bg-cover"
          style={{
            backgroundImage: `url("${Services_Bg}")`,
          }}
        >
          <div className="container mx-auto">
            <div className="mb-8 text-center">
              <h1 className="mb-2 text-2xl md:text-3xl font-bold tracking-tight xl:text-[36px] text-[#FFF]">
                Our Capabilities
              </h1>
              <p className="mx-auto max-w-5xl font-normal text-base text-white">
                End-to-End AI Data Workflows: Annotate, Label, and Evaluate with
                Precision.
              </p>
            </div>

            {/* Capabilities Cards Grid */}
            <div className="grid grid-cols-12 gap-8">
              {capabilities.map((capability) => (
                <div
                  key={capability.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 col-span-12 sm:col-span-6 lg:col-span-4"
                >
                  {/* Image Container */}
                  <div className="relative h-64 flex items-center justify-center p-6">
                    <img
                      src={capability.image}
                      alt={capability.title}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-8">
                    <h3 className="text-2xl font-bold mb-4">
                      {capability.title}
                    </h3>

                    {/* Features List */}
                    <ul className="space-y-3">
                      {capability.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckMark />
                          <span className="text-black">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative w-full bg-white py-[80px] md:py-[100px] overflow-hidden">
          <div className="mx-auto container space-y-12">
            <div className="h-[300px] w-[300px] xl:h-[450px] xl:w-[450px] bg-[#7AA8FF] blur-[900px] absolute top-0 -right-[70%] md:-right-[45%] lg:-right-[15%]"></div>
            <div className="mb-8 text-center">
              <h1 className="mb-2 text-2xl md:text-3xl font-bold tracking-tight xl:text-[36px] text-[#000]">
                Why Partners Choose Us
              </h1>
              <p className="mx-auto max-w-5xl font-normal text-base text-black">
                Most analytics and AI platforms provide generic results that
                lack context and transparency. We built the [AI] Platform
                differently.
              </p>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-y-12 md:gap-x-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="relative flex flex-col items-center text-center bg-[#EDF9FF29] border border-[#EDF9FF] px-6 py-[32px]"
                >
                  {/* Icon */}
                  <img
                    src={feature.icon}
                    alt="icon"
                    className="absolute -top-5"
                  />

                  {/* Title */}
                  <h3 className="text-lg font-bold mb-2">{feature.title}</h3>

                  {/* Description */}
                  <p className="text-base">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="bg-[#EDF1FF] py-[48px] bg-left-bottom md:bg-center bg-cover md:bg-cover"
          style={{
            backgroundImage: `url("${Use_Case_Bg}")`,
          }}
        >
          <div className="container space-y-12">
            <div className="mb-20 text-center">
              <h1 className="mb-2 text-2xl md:text-3xl font-bold tracking-tight xl:text-[36px] text-[#FFF]">
                Partner With Us
              </h1>
            </div>

            <div className="grid grid-cols-12 lg:flex gap-y-8 sm:gap-8 lg:justify-center">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="col-span-12 sm:col-span-6 lg:max-w-[300px] bg-white flex flex-col items-center rounded-xl px-3 py-6 gap-4"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16"
                  />
                  <div className="space-y-2 text-center">
                    <h5 className="text-lg md:text-xl font-bold text-[#020128]">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128]">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <NewFooter>
          <BookConsultation
            title="Let’s help you annotate and label data that AI can use"
            text="We fine-tune models, streamline data, and scale your AI stack
                  effortlessly."
            btnText="Book a Consultation"
          />
        </NewFooter>
      </main>
    </>
  );
}
